import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import baseURL from "./baseURL/BaseURL";
import loginToken from "./localStorageItems/LoginTokin";
import { ToastContainer, toast } from "react-toastify";
const EditInformationForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await baseURL.get("/get_all_countries");
        setCountries(response.data.countries);
      } catch (error) {
        toast.error("Failed to load countries");
      }
    };

    fetchCountries();

    if (!location.state?.profileData) {
      const fetchProfile = async () => {
        try {
          setLoading(true);
          const response = await baseURL.get(`/profile`, {
            headers: {
              Authorization: `Bearer ${loginToken}`,
            },
          });
          if (response.status === 200) {
            setFormData(response.data.data);
            if (response.data.data.world_country_id) {
              fetchDivisions(response.data.data.world_country_id);
            }

            if (response.data.data.world_division_id) {
              fetchCities(response.data.data.world_division_id);
            }

            setLoading(false);
          }
        } catch (error) {
          toast.error("Failed to load profile data");
          setLoading(false);
        }
      };

      if (loginToken) {
        fetchProfile();
      }
    } else {
      setFormData(location.state.profileData);
      if (location.state.profileData.world_country_id) {
        fetchDivisions(location.state.profileData.world_country_id);
      }

      if (location.state.profileData.world_division_id) {
        fetchCities(location.state.profileData.world_division_id);
      }
    }
  }, [location.state]);

  const fetchDivisions = async (countryId) => {
    try {
      const response = await baseURL.get(`/get_world_divisions/${countryId}`);
      setDivisions(response.data.divisions);
    } catch (error) {
      toast.error("Failed to load divisions/states");
    }
  };

  const fetchCities = async (divisionId) => {
    try {
      const response = await baseURL.get(`/get_all_cities/${divisionId}`);
      setCities(response.data.cities);
    } catch (error) {
      toast.error("Failed to load cities");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCountryChange = async (e) => {
    const selectedCountryId = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      world_country_id: selectedCountryId,
      world_division_id: "",
      world_city_id: "",
    }));

    setDivisions([]);
    setCities([]);

    if (selectedCountryId) {
      fetchDivisions(selectedCountryId);
    }
  };

  const handleDivisionChange = async (e) => {
    const selectedDivisionId = e.target.value;

    setFormData((prevData) => ({
      ...prevData,
      world_division_id: selectedDivisionId,
      world_city_id: "",
    }));

    setCities([]);

    if (selectedDivisionId) {
      fetchCities(selectedDivisionId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formDataToSend = new FormData();

    Object.keys(formData).forEach((key) => {
      if (
        formData[key] !== null &&
        formData[key] !== undefined &&
        formData[key] !== ""
      ) {
        formDataToSend.append(`user[${key}]`, formData[key]);
      }
    });
    try {
      const response = await baseURL.put("/users", formDataToSend, {
        headers: {
          Authorization: `Bearer ${loginToken}`,
        },
      });

      if (response.status === 200) {
        toast.success("Profile updated successfully!");
        setTimeout(() => {
          navigate("/profile");
        }, 3000);
      }
    } catch (error) {
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  function formatDisplayText(text) {
    if (!text) return ""; // Handle undefined/null cases
    return text
      .replace(/_/g, " ") // First convert underscores to spaces
      .split(" ") // Split into words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" "); // Join with spaces
  }
  if (loading && Object.keys(formData).length === 0) {
    return (
      <>
        <div className="min-h-screen flex justify-center items-center bg-gray-50">
          <div className="relative w-24 h-24">
            <div className="absolute top-1/2 left-1/2 -ml-8 -mt-8 w-16 h-16 rounded-full bg-red-800 opacity-75 animate-ping"></div>
            <div
              className="absolute top-1/2 left-1/2 -ml-6 -mt-6 w-12 h-12 rounded-full bg-red-700 opacity-75 animate-ping"
              style={{ animationDelay: "0.3s" }}
            ></div>
            <div
              className="absolute top-1/2 left-1/2 -ml-4 -mt-4 w-8 h-8 rounded-full bg-red-500 opacity-75 animate-ping"
              style={{ animationDelay: "0.6s" }}
            ></div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ToastContainer />
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-serif">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
          <h1 className="font-serif text-3xl font-bold text-center text-[#730000] mb-8">
            Edit Profile Information
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Info Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="font-serif text-xl font-bold text-[#730000] mb-4">
                Personal Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Gender
                  </label>
                  <select
                    name="gender"
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  >
                    <option value={formData.gender || ""}>
                      {formatDisplayText(formData.gender) || ""}
                    </option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Marital Status
                  </label>
                  <select
                    name="marital_status"
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  >
                    <option value={formData.marital_status || ""}>
                      {formatDisplayText(formData?.marital_status) || ""}
                    </option>
                    <option value="Single">Single</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widow (Husband Died)">
                      Widowed (Husband Died)
                    </option>
                    <option value="Widower (Wife Died)">
                      Widower (Wife Died)
                    </option>
                    <option value="Divorced">Divorced</option>
                    <option value="Separated">Separated</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Religion
                  </label>
                  <select
                    name="religion"
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  >
                    <option value={formData.religion || ""}>
                      {formatDisplayText(formData?.religion) || ""}
                    </option>
                    <option value="Islam">Islam</option>
                    <option value="Christianity">Christianity</option>
                    <option value="Hinduism">Hinduism</option>
                    <option value="Buddhism">Buddhism</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Caste
                  </label>
                  <input
                    type="text"
                    name="caste"
                    value={formData.caste || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Height
                  </label>
                  <input
                    type="text"
                    name="height"
                    value={formData.height || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Complexion
                  </label>
                  <select
                    name="look"
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  >
                    <option value={formData.look || ""}>
                      {formatDisplayText(formData.look) || ""}
                    </option>
                    <option value="Very Fair">Very Fair</option>
                    <option value="Fair">Fair</option>
                    <option value="Medium">Medium</option>
                    <option value="Dark">Dark</option>
                  </select>
                </div>
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Blood Group
                  </label>
                  <input
                    type="text"
                    name="bloodGroup"
                    value={formData.bloodGroup || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div> */}
              </div>
            </div>

            {/* Location Section - Added */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="font-serif text-xl font-bold text-[#730000] mb-4">
                Location Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Country
                  </label>
                  <select
                    name="world_country_id"
                    value={formData.world_country_id || ""}
                    onChange={handleCountryChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  >
                    <option value="">Select Country</option>
                    {Array.isArray(countries) && countries.length > 0 ? (
                      countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))
                    ) : (
                      <option disabled>No countries available</option>
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    State/Division
                  </label>
                  <select
                    name="world_division_id"
                    value={formData.world_division_id || ""}
                    onChange={handleDivisionChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    disabled={!formData.world_country_id}
                  >
                    <option value="">Select State/Division</option>
                    {divisions.map((division) => (
                      <option key={division.id} value={division.id}>
                        {division.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    City
                  </label>
                  <select
                    name="world_city_id"
                    value={formData.world_city_id || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    disabled={!formData.world_division_id}
                  >
                    <option value="">Select City</option>
                    {cities.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Details Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="font-serif text-xl font-bold text-[#730000] mb-4">
                Contact Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    readOnly
                    value={formData.email || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
              </div>
            </div>

            {/* Family Details Section */}
            {/* <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="font-serif text-xl font-bold text-[#730000] mb-4">
                Family Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Father's Name
                  </label>
                  <input
                    type="text"
                    name="fatherName"
                    value={formData.fatherName || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Father's Job
                  </label>
                  <input
                    type="text"
                    name="fatherJob"
                    value={formData.fatherJob || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Mother's Name
                  </label>
                  <input
                    type="text"
                    name="motherName"
                    value={formData.motherName || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Mother's Job
                  </label>
                  <input
                    type="text"
                    name="motherJob"
                    value={formData.motherJob || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Siblings
                  </label>
                  <input
                    type="text"
                    name="siblings"
                    value={formData.siblings || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
              </div>
            </div> */}

            {/* Education Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="font-serif text-xl font-bold text-[#730000] mb-4">
                Education
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Highest Degree
                  </label>
                  <select
                    name="education"
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  >
                    <option value={formData.education || ""}>
                      {formatDisplayText(formData.education) || ""}
                    </option>
                    <option value="Under Matric">Under Matric</option>
                    <option value="Matric">Matric</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Graduation">Graduation</option>
                    <option value="Masters">Masters</option>
                    <option value="PHD">PHD</option>
                  </select>
                </div>
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700">
                    College/University
                  </label>
                  <input
                    type="text"
                    name="college"
                    value={formData.college || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Graduation Year
                  </label>
                  <input
                    type="text"
                    name="graduationYear"
                    value={formData.graduationYear || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div> */}
              </div>
            </div>

            {/* Career Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="font-serif text-xl font-bold text-[#730000] mb-4">
                Career
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Job
                  </label>
                  <select
                    name="occupation"
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  >
                    <option value={formData.occupation || ""}>
                      {formatDisplayText(formData.occupation) || ""}
                    </option>
                    <option value="Doctor">Doctor</option>
                    <option value="Engineer">Engineer</option>
                    <option value="CSS Officer">CSS Officer</option>
                    <option value="Cyber Security">Cyber Security</option>
                    <option value="Teacher">Teacher</option>
                    <option value="Business Man">Business Man</option>
                    <option value="Armed Forces">Armed Forces</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Work Location
                  </label>
                  <input
                    type="text"
                    name="workLocation"
                    value={formData.workLocation || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Yearly Income
                  </label>
                  <input
                    type="text"
                    name="income"
                    value={formData.income || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
              </div>
            </div>

            {/* Image URL Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="font-serif text-xl font-bold text-[#730000] mb-4">
                Profile Image
              </h2>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Image URL
                </label>
                <input
                  type="text"
                  name="image_url"
                  value={formData.image_url || ""}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
              </div>
              {formData.image_url && (
                <div className="mt-2">
                  <p className="text-sm text-gray-500">Current Image:</p>
                  <img
                    src={formData.image_url}
                    alt="Profile Preview"
                    className="mt-2 w-32 h-32 object-cover rounded-full border-2 border-[#730000]"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={() => navigate("/profile")}
                className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#730000]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#730000] hover:bg-[#5a0000] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#730000] disabled:opacity-50"
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditInformationForm;
